import { isRequestError } from "@prismicio/editor-support/Http"
import { ErrorBoundary, useAlert } from "@prismicio/editor-ui"
import { type PropsWithChildren, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

export function DocumentNotFoundErrorBoundary(props: PropsWithChildren) {
  const { children } = props

  const [error, setError] = useState<unknown>()

  const navigate = useNavigate()
  const { showAlert } = useAlert()

  useEffect(() => {
    if (!error) return

    navigate("/", { replace: true })

    setTimeout(() => {
      showAlert({
        title: "Page not found",
        subtitle: `The page you are looking for does not exist anymore. You have been redirected to the working page list.`,
      })
    }, 250)
  }, [error, navigate, showAlert])

  function onError(error: unknown) {
    setError(error)
  }

  function renderError(error: unknown) {
    // Delegate to the higher level ErrorBoundary.
    // Note that for Error boundaries to work properly, errors must be thrown from render.
    if (!isDocumentNotFoundError(error)) {
      throw error
    }

    // Let our redirect take place in useEffect.
    return null
  }

  return (
    <ErrorBoundary onError={onError} renderError={renderError}>
      {children}
    </ErrorBoundary>
  )
}

function isDocumentNotFoundError(error: unknown): boolean {
  return (
    isRequestError(error) &&
    error.detail.type === "http" &&
    error.detail.status === 404
  )
}
